import { useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import {
  ScheduleRequestReviewStatus,
  ScheduleViewData,
} from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import dayjs from 'dayjs';
import { AnalyticsGatewayV1 } from '@frontend/api-analytics-gateway';
import { ContactUserTableActions, useAnalyticsOrgLocations } from '@frontend/analytics-shared';
import { useTranslation } from '@frontend/i18n';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { Table, TableColumnConfig, TableProps } from '@frontend/design-system';
import { ScheduleRequestChannelLabels } from '../../constants';
import { useIsAppointmentDemoAccount } from '../../hooks/use-is-appointment-demo-account';
import { ScheduleRequestStatusChip } from '../atoms/appointment-status-chips';

const DEFAULT_PAGE_SIZE = 10;
const rowsPerPageOptions = [10, 25, 50, 75, 100];

export interface ScheduleRequestsTableProps {
  locationId?: string[];
  startDate?: string;
  endDate?: string;
  reviewedStatus?: ScheduleRequestReviewStatus[];
  pageSize?: number;
}

const formatDateTime = (value?: string) => (value ? dayjs(value).format('MMM D, YYYY h:mm A') : '-');

export const ScheduleRequestsTable = ({
  locationId,
  startDate,
  endDate,
  reviewedStatus,
  pageSize = DEFAULT_PAGE_SIZE,
}: ScheduleRequestsTableProps) => {
  const { t } = useTranslation('analytics');
  const isDemoAccount = useIsAppointmentDemoAccount();
  const { locationNames } = useAnalyticsOrgLocations({ isDemoAccount, module: 'APPOINTMENT' });

  const [pageConfig, setPageConfig] = useState({ pageNumber: 1, pageSize });

  const isEnabled = !!locationId?.length && !!startDate && !!endDate;

  const queryRequest = useMemo(
    () => ({
      locationId,
      startDate,
      endDate,
      reviewedStatus,
      pageSize: pageConfig.pageSize,
    }),
    [locationId, startDate, endDate, reviewedStatus, pageConfig.pageSize]
  );

  const {
    data: scheduleViewData,
    isLoading,
    isFetching,
    hasNextPage,
    fetchNextPage,
  } = AnalyticsGatewayV1.Queries.useGetScheduleViewQuery(queryRequest, {
    enabled: isEnabled,
  });

  // Reset to page 1 when filters change
  useEffect(() => {
    setPageConfig((prev) => ({ ...prev, pageNumber: 1 }));
  }, [locationId, startDate, endDate, reviewedStatus]);

  useEffect(() => {
    if (!scheduleViewData?.pages[pageConfig.pageNumber - 1]) {
      fetchNextPage();
    }
  }, [scheduleViewData?.pages, fetchNextPage, pageConfig.pageNumber]);

  const items = scheduleViewData?.pages[pageConfig.pageNumber - 1]?.data ?? [];

  const getHasNext = () => {
    const pageData = scheduleViewData?.pages[pageConfig.pageNumber - 1]?.data ?? [];
    return hasNextPage && pageData.length === pageConfig.pageSize;
  };

  const colConfig: TableColumnConfig<ScheduleViewData>[] = useMemo(
    () => [
      {
        id: 'locationId',
        Header: t('Location'),
        accessor: ({ locationId }) => (locationId ? locationNames[locationId] || '-' : '-'),
      },
      {
        id: 'phoneNumber',
        Header: t('Contact Number'),
        accessor: ({ phoneNumber }) => (phoneNumber ? formatPhoneNumber(phoneNumber) : '-'),
        width: 180,
      },
      {
        id: 'requestDate',
        Header: t('Request Created Date'),
        accessor: ({ requestDate, createdAt }) => formatDateTime(requestDate ?? createdAt),
      },
      {
        id: 'channel',
        Header: t('Channel'),
        accessor: ({ channel }) => (channel ? ScheduleRequestChannelLabels[channel] : '-'),
      },
      {
        id: 'requestedSlots',
        Header: t('Requested Slots'),
        accessor: (item) => item,
        cellRenderer: ({ requestedSlots }) => {
          const slots: string[] = requestedSlots ?? [];
          if (!slots.length) return '-';
          return (
            <div css={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {slots.map((slot, index) => (
                <span key={index}>
                  {formatDateTime(slot)}
                  {index < slots.length - 1 && ','}
                </span>
              ))}
            </div>
          );
        },
        disableSortBy: true,
      },
      {
        id: 'reviewedStatus',
        Header: t('Appointment Outcome'),
        accessor: (item) => item,
        cellRenderer: ({ reviewedStatus }) =>
          reviewedStatus ? (
            <span
              css={{
                width: '100%',
                display: 'inline-flex',
              }}
            >
              <ScheduleRequestStatusChip
                status={reviewedStatus}
                css={{
                  maxWidth: '100%',
                }}
              />
            </span>
          ) : (
            '-'
          ),
      },
      {
        id: 'reviewedAt',
        Header: t('Reviewed At'),
        accessor: ({ reviewedAt }) => formatDateTime(reviewedAt),
      },
      {
        Header: 'Actions',
        accessor: (item) => item,
        cellRenderer: (item: ScheduleViewData) => {
          return (
            <ContactUserTableActions isDemoAccount={isDemoAccount} personId='' phoneNumber={item.phoneNumber ?? ''} />
          );
        },
        disableSortBy: true,
        id: 'actions',
        sticky: 'right',
        disableColumnFilter: true,
        width: 'unset',
        minWidth: 120,
      },
    ],
    [t, isDemoAccount, locationNames]
  );

  const tableProps: TableProps<ScheduleViewData> = {
    isLoading: isLoading || isFetching,
    loadingRowsCount: pageConfig.pageSize,
    data: items,
    tableInstanceId: 'schedule-requests-table',
    hasFilterColumns: false,
    hasResizeColumns: false,
    colConfig,
    fullHeight: false,
    isPaginated: true,
    manualPaginationConfig: {
      handleChange: (action: 'next' | 'prev') => {
        if (action === 'next') {
          setPageConfig((prev) => ({ ...prev, pageNumber: prev.pageNumber + 1 }));
        } else {
          setPageConfig((prev) => ({ ...prev, pageNumber: prev.pageNumber - 1 }));
        }
      },
      hasNext: getHasNext(),
      hasPrevious: pageConfig.pageNumber > 1,
      page: pageConfig.pageNumber,
      defaultRowsPerPage: pageConfig.pageSize,
      showNumRowSelector: true,
      onNumRowsChange: (num) => {
        setPageConfig({ pageNumber: 1, pageSize: num });
      },
      rowsPerPageOptions,
    },
  };

  return (
    <div css={styles.container}>
      <Table {...tableProps} />
    </div>
  );
};

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    overflow: hidden;
  `,
};
